import { put } from 'redux-saga/effects';
import { Action, ActionFunction1, ActionFunction0, createAction } from 'redux-actions';
import {
  NetworkRequestState,
  PuppyApiResponseWrapper,
  ErrorDetails,
  RequestMeta,
  SuccessAction,
  FailureAction,
} from './types';
import { isApiError } from '@wix/da-http-client';

export function createNetworkRequestActionSet<
  RequestDataType,
  ResponseDataType,
  SuccessPayload = {},
>(
  reduxKey: string,
  actionType: string,
  options: {
    successPayloadGenerator?: (data: PuppyApiResponseWrapper<ResponseDataType>) => SuccessPayload;
  } = {}
): {
  submit: ActionFunction0<any>;
  success: SuccessAction<ResponseDataType, SuccessPayload>;
  failure: FailureAction<RequestDataType>;
} {
  return {
    submit: createAction(`${reduxKey}.${actionType}`, () => ({})),
    success: createAction(
      `${reduxKey}.${actionType}_SUCCESS`,
      (data: PuppyApiResponseWrapper<ResponseDataType>) => ({
        ...(options.successPayloadGenerator
          ? options.successPayloadGenerator(data)
          : ({} as SuccessPayload)),
      })
    ),
    failure: createAction(
      `${reduxKey}.${actionType}_FAILURE`,
      (errorMessage: string, errors: ErrorDetails<RequestDataType>) => ({
        errorMessage,
        errors,
      })
    ),
  };
}

/**
 * In certain cases, the data for the api request is not stored in redux. This function handles that case by allowing the data to be supplied on the consumer's side.
 */
export function createNetworkRequestActionSetWithSuppliedData<
  RequestDataType,
  ResponseDataType,
  SuccessPayload = {},
>(
  reduxKey: string,
  actionType: string,
  options: {
    successPayloadGenerator?: (data: PuppyApiResponseWrapper<ResponseDataType>) => SuccessPayload;
  } = {}
) {
  const { failure, success } = createNetworkRequestActionSet<
    RequestDataType,
    ResponseDataType,
    SuccessPayload
  >(reduxKey, actionType, options);

  return {
    submit: createAction(`${reduxKey}.${actionType}`, (data: RequestDataType) => ({ data })),
    success,
    failure,
  };
}

export function networkRequestMetaReducer<
  RequestDataType,
  ResponseDataType,
  SuccessPayload = {},
  WidgetState = {},
>({
  initialAction,
  successAction,
  failureAction,
  additionalStateUpdates = {},
  requestDataPlacer,
}: {
  initialAction: ActionFunction1<RequestDataType, any> | ActionFunction0<any>;
  successAction: SuccessAction<ResponseDataType, SuccessPayload>;
  failureAction: FailureAction<RequestDataType>;
  additionalStateUpdates?: {
    initialStateUpdate?: (
      state: WidgetState,
      action: Action<RequestDataType>
    ) => Partial<WidgetState>;
    successStateUpdate?: (
      state: WidgetState,
      action: Action<SuccessPayload>
    ) => Partial<WidgetState>;
    failureStateUpdate?: (
      state: WidgetState,
      action: Action<ErrorDetails<RequestDataType>>
    ) => Partial<WidgetState>;
  };
  /**
   * Directs data to be placed in a different location in the state than the default (default function places it in the `request` field of the widget state).
   */
  requestDataPlacer?: (
    state: WidgetState,
    requestMeta: RequestMeta<RequestDataType>
  ) => Partial<WidgetState>;
}) {
  const additionalInitialStateUpdate = additionalStateUpdates.initialStateUpdate ?? (() => ({}));
  const additionalSuccessStateUpdate = additionalStateUpdates.successStateUpdate ?? (() => ({}));
  const additionalFailureStateUpdate = additionalStateUpdates.failureStateUpdate ?? (() => ({}));
  const requestDataPlacerFunction =
    requestDataPlacer ??
    ((state, data) => ({
      request: {
        ...data,
      },
    }));

  return {
    [initialAction.toString()]: (state, action) => ({
      ...state,
      ...requestDataPlacerFunction(state, {
        status: NetworkRequestState.SENT_AND_WAITING_FOR_RESPONSE,
        errors: {},
      }),
      ...additionalInitialStateUpdate(state, action),
    }),
    [successAction.toString()]: (state, action) => ({
      ...state,
      ...requestDataPlacerFunction(state, {
        status: NetworkRequestState.SUCCESS,
        errors: {},
      }),
      ...additionalSuccessStateUpdate(state, action),
    }),
    [failureAction.toString()]: (state, action) => ({
      ...state,
      ...requestDataPlacerFunction(state, {
        status: NetworkRequestState.FAILURE,
        errorMessage: action.payload.errorMessage,
        errors: action.payload.errors,
      }),
      ...additionalFailureStateUpdate(state, action),
    }),
  };
}

export function* handleNetworkResponse<RequestDataType, ResponseDataType, SuccessPayload = {}>(
  response: PuppyApiResponseWrapper<ResponseDataType>,
  successAction: SuccessAction<ResponseDataType, SuccessPayload>,
  failureAction: FailureAction<RequestDataType>,
  defaultErrorMessage: string
) {
  if (!response || isApiError(response) || response.success === false) {
    const errorMessage = (response && response.errorDescription) || defaultErrorMessage;

    const errors: ErrorDetails<RequestDataType> = response.errorDetails ?? {};
    yield put(failureAction(errorMessage, errors));
    return false;
  }

  yield put(successAction(response));
  return true;
}
